import { type SVGProps } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { type AuthResponse } from '@cx-web/feature-auth';

import styles from './styles/VerifyEmail.module.css';

export type VerifyEmailProps = {
  authBasePath?: string;
  // The address the verification email was sent to (seeded via the auth-modal store's
  // prefillEmail, the same mechanism SignupForm's email field uses). Undefined only if the modal
  // was somehow opened at this view without going through a successful signup.
  email?: string;
  // Whether this render is the non-plus flow (mirrors Authentication/SignupForm's own prop).
  // CX_AUTH_CHECK_EMAIL's real CMS copy is plus-only marketing ("You're in on OC updates. Welcome
  // to the roster.") left over from product-plus's own pre-migration signup screen, not a
  // verify-your-email message — it must not show for non-plus, which currently is the only
  // audience that ever reaches this view (see SignupForm's api-success effect).
  hideInfoPanel?: boolean;
  repubPath: string;
};

export const VerifyEmail = ({ authBasePath = '/plus/auth', email, hideInfoPanel, repubPath }: VerifyEmailProps) => {
  const { t } = useTranslation(['label']);
  const fetcher = useFetcher<AuthResponse>();
  const data = fetcher.data;

  return (
    <div className={styles.verifyEmail} data-testid="verify-email">
      <h2 className={styles.title}>{t('CX_AUTH_VERIFY_EMAIL_HEADING')}</h2>
      <div className={styles.iconContainer}>
        <CheckIcon className={styles.icon} height={50} />
      </div>
      <div className={styles.content}>
        {email && <p className={styles.text}>{t('CX_AUTH_VERIFY_EMAIL_SENT_TO', { email })}</p>}
        {!hideInfoPanel && <p className={styles.text}>{t('CX_AUTH_CHECK_EMAIL')}</p>}
        <fetcher.Form action={`${repubPath}${authBasePath}/resend-verification`} method="post">
          <input name="email" type="hidden" value={email ?? ''} />
          <p className={styles.text}>
            <Trans
              components={{
                resendLink: (
                  // eslint-disable-next-line jsx-a11y/control-has-associated-label -- Trans injects the link text at runtime
                  <button className={styles.resendLink} disabled={fetcher.state !== 'idle'} type="submit" />
                ),
              }}
              i18nKey="CX_AUTH_RESEND_EMAIL_PROMPT"
            />
          </p>
        </fetcher.Form>
        {data && (
          <p className={data.success ? styles.text : styles.errorMessage} data-testid="resend-verification-response">
            {data.success ? t('CX_AUTH_RESEND_EMAIL_SUCCESS') : t('CX_AUTH_RESEND_EMAIL_ERROR')}
          </p>
        )}
      </div>
    </div>
  );
};

VerifyEmail.displayName = 'VerifyEmail';

function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...props} fill="none" height="74" viewBox="0 0 74 74" width="74" xmlns="http://www.w3.org/2000/svg">
      <circle cx="37" cy="37" fill="#00E889" r="37" />
      <path d="M16 39.0682L28.5568 51.625L56.1818 24" stroke="#022E4A" strokeLinecap="round" strokeWidth="4" />
    </svg>
  );
}
